/** Sessão: o token de acesso vive em memória e é renovado pelo cookie de refresh.
 * Ao abrir o app, tenta renovar antes de decidir entre login e painel.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as api from "./api";
import type { User } from "./types";

type State = "carregando" | "fora" | "dentro";

interface SessionValue {
  state: State;
  user: User | null;
  entrar: (email: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
}

const Ctx = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>("carregando");
  const [user, setUser] = useState<User | null>(null);
  // O `refresh()` do boot é assíncrono e pode chegar DEPOIS de um login manual. Quando
  // chegava com `null` (aba nova, sem cookie), ele escrevia "fora" por cima de uma sessão
  // que já estava dentro — o app voltava para a tela de login logo após entrar. Sob
  // lentidão de rede a janela alarga e vira regra em vez de exceção.
  const jaResolvida = useRef(false);

  useEffect(() => {
    api.setUnauthorizedHandler(() => {
      setUser(null);
      setState("fora");
    });
    api
      .refresh()
      .then((u) => {
        if (jaResolvida.current) return;
        setUser(u);
        setState(u ? "dentro" : "fora");
      })
      .catch(() => {
        if (!jaResolvida.current) setState("fora");
      });
  }, []);

  const entrar = useCallback(async (email: string, senha: string) => {
    const u = await api.login(email, senha);
    jaResolvida.current = true;
    setUser(u);
    setState("dentro");
  }, []);

  const sair = useCallback(async () => {
    await api.logout();
    jaResolvida.current = true;
    setUser(null);
    setState("fora");
  }, []);

  return <Ctx.Provider value={{ state, user, entrar, sair }}>{children}</Ctx.Provider>;
}

export function useSession() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession fora do SessionProvider");
  return v;
}
